import { getTextLines } from '../text/utils';
import { isDef, isStr } from './common';

export type DupeBehavior = 'throw' | 'ignore' | 'overwrite' | 'ignore-if-same-value';

type KeyFn<I, K> = (item: I, index: number) => K;
type ValFn<I, V> = (item: I, index: number) => V;
const identity = <I>(item: I): I => item;

export function keyInto<I, K>(map: Map<K, I>, items: readonly I[], keyFn: KeyFn<I, K>): Map<K, I>;
export function keyInto<I, K>(
	map: Map<K, I>,
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_dupeBehavior: DupeBehavior
): Map<K, I>;
export function keyInto<I, K, V>(
	map: Map<K, V>,
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn: ValFn<I, V>
): Map<K, V>;
export function keyInto<I, K, V>(
	map: Map<K, V>,
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn: ValFn<I, V>,
	_dupeBehavior: DupeBehavior
): Map<K, V>;
export function keyInto<I, K, V>(
	map: Map<K, V>,
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn?: ValFn<I, V> | DupeBehavior,
	_dupeBehavior?: DupeBehavior
): Map<K, V>;
export function keyInto<I, K, V>(
	map: Map<K, V>,
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn?: ValFn<I, V> | DupeBehavior,
	_dupeBehavior?: DupeBehavior
): Map<K, V> {
	const dupeBehavior = _dupeBehavior || (isStr(_valFn) ? _valFn : 'throw');
	const valFn: ValFn<I, V> =
		isDef(_valFn) && !isStr(_valFn)
			? _valFn
			: // Identity
			  (identity as ValFn<I, V>);

	const len = items.length;
	for (let index = 0; index < len; ++index) {
		const item = items[index];

		const key = keyFn(item, index);
		const val = valFn(item, index);
		if (map.has(key)) {
			if (dupeBehavior === 'throw') {
				throw new Error(`keyInto: key already exists ("${key}")`);
			} else if (dupeBehavior === 'overwrite') {
				map.set(key, val);
			} else if (dupeBehavior === 'ignore-if-same-value') {
				const existing = map.get(key);
				if (existing !== val) {
					throw new Error(`keyInto: key already exists with different value ("${key}")`);
				}
			}
		} else {
			map.set(key, val);
		}
	}

	return map;
}

export function keyBy<I, K>(items: readonly I[], keyFn: KeyFn<I, K>): Map<K, I>;
export function keyBy<I, K>(
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_dupeBehavior: DupeBehavior
): Map<K, I>;
export function keyBy<I, K, V>(
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn: ValFn<I, V>
): Map<K, V>;
export function keyBy<I, K, V>(
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn: ValFn<I, V>,
	_dupeBehavior: DupeBehavior
): Map<K, V>;
export function keyBy<I, K, V>(
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn?: ValFn<I, V> | DupeBehavior,
	_dupeBehavior?: DupeBehavior
): Map<K, V> {
	const map: Map<K, V> = new Map();
	return keyInto(map, items, keyFn, _valFn, _dupeBehavior);
}

export function isUniqueBy<I, K>(items: readonly I[], keyFn: KeyFn<I, K>): boolean {
	const keys: Set<K> = new Set();

	const len = items.length;
	for (let index = 0; index < len; ++index) {
		const item = items[index];

		const key = keyFn(item, index);
		if (keys.has(key)) {
			return false;
		}
		keys.add(key);
	}

	return true;
}

export function uniqueBy<I, K>(items: readonly I[], keyFn: KeyFn<I, K>): I[];
export function uniqueBy<I, K, V>(
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn: ValFn<I, V>
): V[];
export function uniqueBy<I, K, V>(
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn?: ValFn<I, V>
): V[] {
	const valFn: ValFn<I, V> =
		isDef(_valFn) && !isStr(_valFn)
			? _valFn
			: // Identity
			  (identity as ValFn<I, V>);

	const keys: Set<K> = new Set();
	const result: V[] = [];

	const len = items.length;
	for (let index = 0; index < len; ++index) {
		const item = items[index];

		const key = keyFn(item, index);
		if (!keys.has(key)) {
			const val = valFn(item, index);
			result.push(val);

			keys.add(key);
		}
	}

	return result;
}

export function groupBy<I, K>(items: Iterable<I>, keyFn: KeyFn<I, K>): Map<K, I[]>;
export function groupBy<I, K, V>(
	items: Iterable<I>,
	keyFn: KeyFn<I, K>,
	_valFn: ValFn<I, V>,
	uniqueOnly?: boolean
): Map<K, V[]>;
export function groupBy<I, K, V>(
	items: Iterable<I>,
	keyFn: KeyFn<I, K>,
	_valFn?: ValFn<I, V>,
	uniqueOnly = false
): Map<K, V[]> {
	const valFn: ValFn<I, V> =
		isDef(_valFn) && !isStr(_valFn)
			? _valFn
			: // Identity
			  (identity as ValFn<I, V>);

	const map: Map<K, V[]> = new Map();

	let index = -1;
	for (const item of items) {
		++index;

		const key = keyFn(item, index);
		let group = map.get(key);
		if (!group) {
			group = [];
			map.set(key, group);
		}

		const val = valFn(item, index);

		if (!uniqueOnly || group.indexOf(val) === -1) {
			group.push(val);
		}
	}

	return map;
}

export function groupDupesBy<I, K>(items: readonly I[], keyFn: KeyFn<I, K>): Map<K, I[]>;
export function groupDupesBy<I, K, V>(
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn: ValFn<I, V>
): Map<K, V[]>;
export function groupDupesBy<I, K, V>(
	items: readonly I[],
	keyFn: KeyFn<I, K>,
	_valFn?: ValFn<I, V>
): Map<K, V[]> {
	const grouped = groupBy<I, K, V>(items, keyFn, _valFn as any);

	for (const [key, group] of grouped) {
		if (group.length <= 1) {
			grouped.delete(key);
		}
	}

	return grouped;
}

export function countBy<I>(items: readonly I[]): Map<I, number>;
export function countBy<I, K>(items: readonly I[], _keyFn: KeyFn<I, K>): Map<K, number>;
export function countBy<I, K>(items: readonly I[], _keyFn?: KeyFn<I, K>): Map<K, number> {
	const keyFn: KeyFn<I, K> =
		isDef(_keyFn) && !isStr(_keyFn)
			? _keyFn
			: // Identity
			  (identity as KeyFn<I, K>);

	const map: Map<K, number> = new Map();

	const len = items.length;
	for (let index = 0; index < len; ++index) {
		const item = items[index];
		const key = keyFn(item, index);
		map.set(key, (map.get(key) || 0) + 1);
	}

	return map;
}

export function toLengthMap<K, V>(map: Map<K, V[]>): Map<K, number> {
	const lengths: Map<K, number> = new Map();
	for (const [key, vals] of map) {
		lengths.set(key, vals.length);
	}

	return lengths;
}

export function pickKeys<K, V>(map: Map<K, V>, keys: K[] | Set<K>): Map<K, V> {
	const map2: Map<K, V> = new Map();
	for (const key of keys) {
		if (map.has(key)) {
			map2.set(key, map.get(key) as V);
		}
	}

	return map2;
}

export function doKeyInc<T>(map: Map<T, number>, val: T, inc = 1): Map<T, number> {
	const newValue = (map.get(val) ?? 0) + inc;
	map.set(val, newValue);
	return map;
}

export function doKeyPush<T, U>(map: Map<T, U[]>, key: T, item: U): U[] {
	let list = map.get(key);
	if (!list) {
		list = [item];
		map.set(key, list);
	} else {
		list.push(item);
	}
	return list;
}

export function doKeyAdd<T, U>(map: Map<T, Set<U>>, key: T, item: U): Set<U> {
	let set = map.get(key);
	if (!set) {
		set = new Set([item]);
		map.set(key, set);
	} else {
		set.add(item);
	}
	return set;
}

export function doKeyConcat<T, U>(map: Map<T, U[]>, key: T, items: U[]): Map<T, U[]> {
	let ary = map.get(key);
	if (!ary) {
		ary = [];
		map.set(key, ary);
	}

	for (const val of items) {
		ary.push(val);
	}

	return map;
}

export function doKeyUnion<T, U>(map: Map<T, Set<U>>, key: T, items: Iterable<U>): Map<T, Set<U>> {
	let set = map.get(key);
	if (!set) {
		set = new Set();
		map.set(key, set);
	}

	for (const val of items) {
		set.add(val);
	}

	return map;
}

export function doKeySum<T>(map: Map<T, number>, map2: Map<T, number>): Map<T, number> {
	for (const [key, count] of map2) {
		const newValue = (map.get(key) ?? 0) + count;
		map.set(key, newValue);
	}
	return map;
}

export function doKeyMerge<T, U>(map: Map<T, U[]>, map2: Map<T, U[]>): Map<T, U[]> {
	for (const [key, vals] of map2) {
		let ary = map.get(key);
		if (!ary) {
			ary = [];
			map.set(key, ary);
		}

		for (const val of vals) {
			ary.push(val);
		}
	}
	return map;
}

export const sortLargeToSmall = <T>([, a]: [T, number], [, b]: [T, number]): number => b - a;

export function sortEntriesDesc<T>(map: Map<T, number>): [T, number][] {
	const sorted = [...map.entries()].sort(sortLargeToSmall);
	return sorted;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function sortAndPrint2<T extends Object | string | undefined>(
	map: Map<T, number>,
	sort: (a: [T, number], b: [T, number]) => number,
	toString?: (key: T) => string,
	log?: (str: string) => void
): void {
	log = log ?? console.log;

	const sortedWithStringKey = [...map.entries()].sort(sort).map(([k, count]) => {
		let key: string;
		if (toString) {
			key = toString(k);
		} else if (k?.toString) {
			key = k.toString();
		} else if (!isDef(k)) {
			key = '<undefined>';
		} else if (k === null) {
			key = '<null>';
		} else {
			key = '<unknown>';
		}

		return [key, count] as [string, number];
	});

	let longestKeyLine = 0;
	for (const [k] of sortedWithStringKey) {
		const lines = getTextLines(k).map(line => line.replace(/\t/g, '    '));
		longestKeyLine = Math.max(longestKeyLine, ...lines.map(s => s.length));
	}

	for (const [k, v] of sortedWithStringKey) {
		const lines = getTextLines(k).map(line => line.replace(/\t/g, '    '));
		const last = lines.pop() ?? '';
		for (const line of lines) {
			log(`${line.padStart(longestKeyLine, ' ')}`);
		}
		log(`${last!.padStart(longestKeyLine, ' ')}: ${v.toLocaleString()}`);
	}
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function sortAndPrint<T extends Object | string | undefined>(
	map: Map<T, number>,
	toString?: (key: T) => string,
	log?: (str: string) => void
): void {
	return sortAndPrint2(map, sortLargeToSmall, toString, log);
}
